import type { StsIconName } from '@packages/ui/shared';
import type { ReactNode } from 'react';
import type { TextStyle } from 'react-native';

import { EmptyList } from '@packages/ui/shared/empty-list';
import { Children } from 'react';
import { View } from 'react-native';

import { ssm5ControlStyles, ssm5TextStyles } from '../ssm5.styles';

/** Props for {@link Ssm5PanelMessage}. */
export type Ssm5PanelMessageProps = {
    /** The action under the message, if the technician can do something about it. */
    action?: ReactNode;
    /** The text under the title. */
    description?: string;
    /** The icon over the title, shown large in its circle. */
    icon: StsIconName;
    /** The widest the texts may be, in pixels; the panel's own limit when absent, no limit on a page. */
    textWidth?: number;
    /** The title. */
    title: string;
    /** Whether the message fills a page or a panel. */
    variant?: 'page' | 'panel';
};

/** The size of the message icon. */
const ICON_SIZE = 80;

/**
 * A message filling a panel or page: an icon in a circle over a title, a description and an optional
 * action. The panel limits its texts to 320px, or to the width given; the page variant lifts that limit.
 *
 * @param props - {@link Ssm5PanelMessageProps}
 * @returns the message
 */
export function Ssm5PanelMessage({
    action,
    description,
    icon,
    textWidth,
    title,
    variant = 'panel',
}: Ssm5PanelMessageProps) {
    const textWidthStyle = textWidthStyleOf(variant, textWidth);
    const titleSpacing = description ? ssm5TextStyles.messageTitleWithDescription : undefined;
    const hasAction = Children.toArray(action).length > 0;

    return (
        <EmptyList
            description={description}
            heroContainerStyle={ssm5ControlStyles.messageIllustration}
            iconColor="grayFive"
            iconName={icon}
            iconSize={ICON_SIZE}
            styles={{
                container: ssm5ControlStyles.messageContainer,
                description: [ssm5TextStyles.messageText, textWidthStyle],
                icon: ssm5ControlStyles.messageIcon,
                title: [ssm5TextStyles.messageTitle, textWidthStyle, titleSpacing],
            }}
            textColor="textDarkSecondary"
            title={title}
        >
            {hasAction && <View style={ssm5ControlStyles.messageAction}>{action}</View>}
        </EmptyList>
    );
}

/**
 * The width limit of the texts: the one given, else the panel's own, else none.
 *
 * @param variant - whether the message fills a page or a panel
 * @param textWidth - the widest the texts may be, when given
 * @returns the style limiting the texts, or undefined for no limit
 */
function textWidthStyleOf(variant: 'page' | 'panel', textWidth: number | undefined): TextStyle | undefined {
    if (textWidth !== undefined) {
        return { maxWidth: textWidth };
    }

    return variant === 'panel' ? ssm5TextStyles.messagePanelText : undefined;
}
